class WikipediaService {
    constructor() {
        this.cache = new Map();
        this.baseUrl = 'https://en.wikipedia.org/api/rest_v1';
        this.searchUrl = 'https://en.wikipedia.org/w/api.php';
        
        // Map data categories to Wikipedia article titles
        this.articleMappings = {
            // Economic metrics
            'medianIncome': ['Household_income_in_the_United_States', 'Median_income'],
            'unemployment': ['Unemployment_in_the_United_States', 'List_of_U.S._states_by_unemployment_rate'],
            'gdpPerCapita': ['List_of_U.S._states_by_GDP_per_capita', 'Gross_domestic_product'],
            'minWage': ['Minimum_wage_in_the_United_States', 'List_of_US_states_by_minimum_wage'],
            'stateSalesTax': ['Sales_taxes_in_the_United_States', 'State_tax_levels_in_the_United_States'],
            'stateIncomeTax': ['State_income_tax', 'Income_tax_in_the_United_States'],
            
            // Demographics
            'population': ['List_of_U.S._states_by_population', 'Demographics_of_the_United_States'],
            'populationDensity': ['List_of_U.S._states_by_population_density'],
            'medianAge': ['List_of_U.S._states_by_age', 'Aging_of_the_United_States'],
            'diversityIndex': ['List_of_U.S._states_by_racial_and_ethnic_diversity'],
            'birthRate': ['Birth_rate', 'List_of_U.S._states_by_birth_rate'],
            
            // Education
            'highSchoolGradRate': ['Educational_attainment_in_the_United_States', 'High_school_graduation_rate'],
            'bachelorsDegreeRate': ['List_of_U.S._states_by_educational_attainment'],
            'pupilTeacherRatio': ['Student–teacher_ratio', 'Education_in_the_United_States'],
            'perPupilSpending': ['Public_school_funding_in_the_United_States'],
            
            // Health
            'lifeExpectancy': ['List_of_U.S._states_by_life_expectancy', 'Life_expectancy'],
            'obesityRate': ['Obesity_in_the_United_States', 'List_of_U.S._states_by_obesity_rate'],
            'smokingRate': ['Smoking_in_the_United_States', 'List_of_U.S._states_by_smoking_rate'],
            'infantMortalityRate': ['Infant_mortality', 'List_of_U.S._states_by_infant_mortality_rate'],
            'healthcareQualityRank': ['Health_care_in_the_United_States', 'Health_insurance_coverage_in_the_United_States'],
            
            // Housing
            'homeValue': ['List_of_U.S._states_by_home_ownership_rate', 'Housing_in_the_United_States'],
            'medianRent': ['Housing_in_the_United_States', 'Affordable_housing'],
            'homeOwnershipRate': ['List_of_U.S._states_by_home_ownership_rate'],
            'housingAffordabilityIndex': ['Housing_affordability_index'],
            
            // Environment
            'avgTemperature': ['Climate_of_the_United_States', 'List_of_U.S._states_by_temperature'],
            'airQualityIndex': ['Air_quality_index', 'Air_pollution_in_the_United_States'],
            'renewableEnergyPercent': ['Renewable_energy_in_the_United_States', 'List_of_U.S._states_by_renewable_energy_production'],
            'carbonFootprint': ['Greenhouse_gas_emissions_by_the_United_States', 'Carbon_footprint'],
            
            // Crime
            'violentCrimeRate': ['Crime_in_the_United_States', 'List_of_U.S._states_by_violent_crime_rate'],
            'propertyyCrimeRate': ['Property_crime', 'List_of_U.S._states_by_property_crime_rate'],
            'prisonIncarceration': ['Incarceration_in_the_United_States', 'List_of_U.S._states_by_incarceration_rate'],
            
            // Infrastructure
            'internetSpeed': ['Internet_in_the_United_States', 'Broadband_universal_service'],
            'roadQualityRank': ['Transportation_in_the_United_States', 'Interstate_Highway_System'],
            'bridgeCondition': ['List_of_bridges_in_the_United_States', 'Infrastructure_report_card'],
            'publicTransitScore': ['Public_transportation_in_the_United_States'],
            
            // Politics
            'voterTurnout2020': ['2020_United_States_presidential_election', 'Voter_turnout_in_United_States_presidential_elections'],
            'governmentEfficiency': ['State_governments_of_the_United_States'],
            'corruptionRank': ['Corruption_in_the_United_States', 'Political_corruption'],
            
            // Legal/Social
            'marijuana': ['Legality_of_cannabis_by_U.S._jurisdiction', 'Cannabis_in_the_United_States'],
            'deathPenalty': ['Capital_punishment_in_the_United_States', 'List_of_U.S._states_by_death_penalty_status'],
            'ageOfConsent': ['Ages_of_consent_in_the_United_States'],
            'gunLawStrength': ['Gun_laws_in_the_United_States_by_state', 'Gun_politics_in_the_United_States']
        };

        // State-specific Wikipedia articles
        this.stateArticles = {
            'AL': 'Alabama',
            'AK': 'Alaska',
            'AZ': 'Arizona',
            'AR': 'Arkansas',
            'CA': 'California',
            'CO': 'Colorado',
            'CT': 'Connecticut',
            'DE': 'Delaware',
            'FL': 'Florida',
            'GA': 'Georgia_(U.S._state)',
            'HI': 'Hawaii',
            'ID': 'Idaho',
            'IL': 'Illinois',
            'IN': 'Indiana',
            'IA': 'Iowa',
            'KS': 'Kansas',
            'KY': 'Kentucky',
            'LA': 'Louisiana',
            'ME': 'Maine',
            'MD': 'Maryland',
            'MA': 'Massachusetts',
            'MI': 'Michigan',
            'MN': 'Minnesota',
            'MS': 'Mississippi',
            'MO': 'Missouri',
            'MT': 'Montana',
            'NE': 'Nebraska',
            'NV': 'Nevada',
            'NH': 'New_Hampshire',
            'NJ': 'New_Jersey',
            'NM': 'New_Mexico',
            'NY': 'New_York_(state)',
            'NC': 'North_Carolina',
            'ND': 'North_Dakota',
            'OH': 'Ohio',
            'OK': 'Oklahoma',
            'OR': 'Oregon',
            'PA': 'Pennsylvania',
            'RI': 'Rhode_Island',
            'SC': 'South_Carolina',
            'SD': 'South_Dakota',
            'TN': 'Tennessee',
            'TX': 'Texas',
            'UT': 'Utah',
            'VT': 'Vermont',
            'VA': 'Virginia',
            'WA': 'Washington_(state)',
            'WV': 'West_Virginia',
            'WI': 'Wisconsin',
            'WY': 'Wyoming'
        };
    }

    /**
     * Check if Wikipedia has an article for a specific metric
     */
    async checkMetricArticle(metricKey) {
        const cacheKey = `metric_${metricKey}`;
        
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const articles = this.articleMappings[metricKey];
        if (!articles || articles.length === 0) {
            return null;
        }

        try {
            // Check the first article in the list
            const primaryArticle = articles[0];
            const exists = await this.checkArticleExists(primaryArticle);
            
            if (exists) {
                const result = {
                    available: true,
                    title: primaryArticle.replace(/_/g, ' '),
                    url: `https://en.wikipedia.org/wiki/${primaryArticle}`,
                    articles: articles.map(a => ({
                        title: a.replace(/_/g, ' '),
                        url: `https://en.wikipedia.org/wiki/${a}`
                    }))
                };
                
                this.cache.set(cacheKey, result);
                return result;
            }
        } catch (error) {
            console.error(`Error checking Wikipedia for ${metricKey}:`, error);
        }

        return null;
    }

    /**
     * Check if Wikipedia has state-specific information
     */
    async checkStateArticle(stateCode, metricKey = null) {
        const cacheKey = `state_${stateCode}_${metricKey || 'general'}`;
        
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const stateArticle = this.stateArticles[stateCode];
        if (!stateArticle) {
            return null;
        }

        let url = `https://en.wikipedia.org/wiki/${stateArticle}`;
        let title = stateArticle.replace(/_/g, ' ');

        // Add section anchors for specific metrics
        const sectionAnchors = {
            'population': '#Demographics',
            'medianIncome': '#Economy',
            'unemployment': '#Economy',
            'education': '#Education',
            'health': '#Healthcare',
            'crime': '#Crime_and_law_enforcement',
            'government': '#Government_and_politics',
            'environment': '#Geography_and_environment'
        };

        // Find the best section for the metric
        if (metricKey) {
            for (const [key, anchor] of Object.entries(sectionAnchors)) {
                if (metricKey.toLowerCase().includes(key)) {
                    url += anchor;
                    break;
                }
            }
        }

        const result = {
            available: true,
            title: title,
            url: url,
            stateSpecific: true
        };

        this.cache.set(cacheKey, result);
        return result;
    }

    /**
     * Search Wikipedia for relevant articles
     */
    async searchWikipedia(query, limit = 5) {
        try {
            const params = new URLSearchParams({
                action: 'opensearch',
                search: query,
                limit: limit,
                format: 'json',
                origin: '*'
            });

            const response = await fetch(`${this.searchUrl}?${params}`);
            const data = await response.json();

            if (data && data[1] && data[1].length > 0) {
                const results = [];
                for (let i = 0; i < data[1].length; i++) {
                    results.push({
                        title: data[1][i],
                        description: data[2][i],
                        url: data[3][i]
                    });
                }
                return results;
            }
        } catch (error) {
            console.error('Wikipedia search error:', error);
        }

        return [];
    }

    /**
     * Check if a Wikipedia article exists
     */
    async checkArticleExists(articleTitle) {
        try {
            const response = await fetch(
                `${this.baseUrl}/page/summary/${encodeURIComponent(articleTitle)}`,
                { method: 'HEAD' }
            );
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    /**
     * Get a summary of a Wikipedia article
     */
    async getArticleSummary(articleTitle) {
        try {
            const response = await fetch(
                `${this.baseUrl}/page/summary/${encodeURIComponent(articleTitle)}`
            );
            
            if (response.ok) {
                const data = await response.json();
                return {
                    title: data.title,
                    extract: data.extract,
                    thumbnail: data.thumbnail,
                    url: data.content_urls.desktop.page
                };
            }
        } catch (error) {
            console.error('Error fetching Wikipedia summary:', error);
        }

        return null;
    }

    /**
     * Get Wikipedia verification status for multiple metrics
     */
    async getVerificationStatus(metrics) {
        const results = {};
        
        for (const metric of metrics) {
            const article = await this.checkMetricArticle(metric);
            results[metric] = {
                hasWikipedia: !!article,
                ...article
            };
        }

        return results;
    }

    /**
     * Clear the cache
     */
    clearCache() {
        this.cache.clear();
    }
}

// Create singleton instance
const wikipediaService = new WikipediaService();

export default wikipediaService;